import {useMemo, useCallback, useState} from "react";
import * as Sentry from "@sentry/react";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {bookingAPI} from "../common/api/bookingAPI";
import {API_RESERVATIONS, API_RESERVATIONS_CONTACT_HOST, API_RESERVATIONS_GUESTS} from "../common/api/apiConstants";
import {createURLQueryFromParams} from "../common/api/apiUtil";

export const messageStatuses = {
    IDLE: "idle",
    SENDING: "sending",
    SUCCEEDED: "succeeded",
    FAILED: "failed"
};

export const useReservation = () => {
    const {initialized, authenticated} = useKeycloakFunctions();
    
    const [reservationData, setReservationData] = useState();
    const [loadingReservationData, setLoadingReservationData] = useState(false);
    const [errorReservationData, setErrorReservationData] = useState();

    const [reservationCosts, setReservationCosts] = useState();
    const [loadingReservationCosts, setLoadingReservationCosts] = useState(false);
    const [errorReservationCosts, setErrorReservationCosts] = useState();

    const [loadingReservationCancel, setLoadingReservationCancel] = useState(false);
    const [errorReservationCancel, setErrorReservationCancel] = useState(null);

    const [messageSendingStatus, setMessageSendingStatus] = useState(messageStatuses.IDLE);
    const [messageToResort, setMessageToResort] = useState("");

    const [loadingChangeNumberOfGuests, setLoadingChangeNumberOfGuests] = useState(false);
    const [errorChangeNumberOfGuests, setErrorChangeNumberOfGuests] = useState(false);
    const [successChangeNumberOfGuests, setSuccessChangeNumberOfGuests] = useState(false);


    const getReservationData = useCallback((reservation_id, locale, exclude) => {
        if (initialized && authenticated) {
            setLoadingReservationData(true);

            return bookingAPI.get(`${API_RESERVATIONS}/${reservation_id}${createURLQueryFromParams({locale, exclude})}`, {redirectOnError: true})
                .then(res => {
                    const data = res?.data;
                    const reservation_signature = res.headers["x-reservation-signature"];

                    if (data) {
                        setReservationData({...data, reservation_signature});
                    }
                    return data;
                })
                .catch(err => {
                    setErrorReservationData(err);
                    Sentry.captureException(err.response);
                    throw err;
                })
                .finally(() => setLoadingReservationData(false));
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const getReservationCosts = useCallback((reservation_id, locale) => {
        if (initialized && authenticated) {
            setLoadingReservationCosts(true);

            let params = {
                locale
            };

            return bookingAPI.get(`${API_RESERVATIONS}/${reservation_id}/costs${createURLQueryFromParams(params)}`, {redirectOnError: false})
                .then(res => {
                    const data = res?.data;
                    if (data) {
                        setReservationCosts(data);
                    }
                    return data;
                })
                .catch(err => {
                    setErrorReservationCosts(err);
                    Sentry.captureException(err.response);
                    throw err;
                })
                .finally(() => setLoadingReservationCosts(false));
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const cancelReservation = (reservation_id, resort_id) => {
        if (initialized && authenticated) {
            setLoadingReservationCancel(true);
            setErrorReservationCancel(null);

            const body = {
                resort_id
            };

            return bookingAPI.post(`${API_RESERVATIONS}/${reservation_id}/cancel`, body)
                .then(res => {
                    setLoadingReservationCancel(false);
                    return res;
                })
                .catch(err => {
                    setLoadingReservationCancel(false);
                    setErrorReservationCancel(err?.response);
                    throw err.response;
                });
        }
    };

    const sendMessageToResort = useCallback((reservation_id, hostName, contactEmail) => {
        if (initialized && authenticated) {
            setMessageSendingStatus(messageStatuses.SENDING);
            return bookingAPI
                .post(`${API_RESERVATIONS}/${reservation_id}${API_RESERVATIONS_CONTACT_HOST}`, {
                    message: messageToResort,
                    host_name: hostName,
                    contact_email: contactEmail
                })
                .then(() => {
                    setMessageSendingStatus(messageStatuses.SUCCEEDED);
                    setMessageToResort("");
                })
                .catch(err => {
                    setMessageSendingStatus(messageStatuses.FAILED);
                    throw err;
                })
                .finally(() => {
                    setTimeout(() => {
                        setMessageSendingStatus(messageStatuses.IDLE);
                    }, 3000);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated, messageToResort]);

    const changeNumberOfGuests = useCallback(async (reservation_id, numberOfGuests) => {
        if (initialized && authenticated) {
            try {
                setSuccessChangeNumberOfGuests();
                setLoadingChangeNumberOfGuests(true);

                await bookingAPI.put(`${API_RESERVATIONS}/${reservation_id}${API_RESERVATIONS_GUESTS}`, {
                    number_of_guests: numberOfGuests
                });
                setSuccessChangeNumberOfGuests(true);
            } catch (err) {
                setErrorChangeNumberOfGuests(err);
                setSuccessChangeNumberOfGuests(false);
            } finally {
                setLoadingChangeNumberOfGuests(false);
            }
        }

    }, [initialized, authenticated]);

    const instance = useMemo(
        () => ({
            getReservation: getReservationData,
            reservation: reservationData,
            loadingReservation: loadingReservationData,
            errorReservation: errorReservationData,

            getReservationCosts,
            reservationCosts,
            loadingReservationCosts,
            errorReservationCosts,

            cancelReservation,
            loadingReservationCancel,
            errorReservationCancel,

            sendMessageToResort,
            messageSendingStatus,
            setMessageSendingStatus,
            messageToResort,
            setMessageToResort,
            messageStatuses,

            changeNumberOfGuests,
            loadingChangeNumberOfGuests,
            errorChangeNumberOfGuests,
            successChangeNumberOfGuests
        }),
        [
            getReservationData,
            reservationData,
            loadingReservationData,
            errorReservationData,

            getReservationCosts,
            reservationCosts,
            loadingReservationCosts,
            errorReservationCosts,

            cancelReservation,
            loadingReservationCancel,
            errorReservationCancel,

            sendMessageToResort,
            messageSendingStatus,
            setMessageSendingStatus,
            messageToResort,
            setMessageToResort,

            changeNumberOfGuests,
            loadingChangeNumberOfGuests,
            errorChangeNumberOfGuests,
            successChangeNumberOfGuests
        ]
    );

    return instance;
};
